import React, { useEffect } from "react";
import type { MenuProps } from "antd";
import { Dropdown, notification, Typography, Flex } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import "./index.css";
import { ROUTES } from "../../../config/routes";
import { ReducerState } from "../../../types";
import { ConnectedProps, connect } from "react-redux";
import { ReactComponent as VectechTextIconSvg } from "../../../assets/icons/Vectech Text - Main v2.svg";
import { ReactComponent as UserSvg } from "../../../assets/icons/userIcon.svg";
import { ReactComponent as UKHealthSecurityAgencySvg } from "../../../assets/icons/UKHealthSecurityAgency.svg";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import OrgSelector from "./OrgSelector";
import { readSuperUser } from "../../../utils/activeOrg";

const { Title } = Typography;

const mapStateToProps = (state: ReducerState) => {
  return {
    notifications: state.notifications,
  };
};

const connector = connect(mapStateToProps, {});

type Props = ConnectedProps<typeof connector> & {
  // Extra props go here
};

const Navbar: React.FC<Props> = ({ notifications }) => {
  const { user, isAuthenticated, logout } = useAuth0();
  const [api, contextHolder] = notification.useNotification();
  const location = useLocation();

  useEffect(() => {
    getNotify();
    // eslint-disable-next-line
  }, [notifications?.count]);

  const getNotify = () => {
    const notiArray = Object.values(notifications?.notifications);
    if (notiArray?.length > 0) {
      api[notiArray[notiArray?.length - 1].severity]({
        message: "Notification",
        description: notiArray[notiArray?.length - 1].message,
        duration: 5,
      });
    }
  };

  const logoutWithRedirect = () =>
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });

  const items: MenuProps["items"] = [
    {
      label: (
        <a
          href="/#"
          onClick={(e) => {
            e.preventDefault();
            logoutWithRedirect();
          }}
        >
          Logout
        </a>
      ),
      key: "1",
    },
  ];

  const supportItems: MenuProps["items"] = [
    // {
    //   label: "Guide",
    //   key: "guide",
    //   disabled: true,
    // },
    // {
    //   label: "Help",
    //   key: "help",
    //   disabled: true,
    // },
    {
      label: (
        <a
          href="https://grove-jumper-74b.notion.site/20e858760a4a8042859fe6703de82af2"
          target="_blank"
          rel="noopener noreferrer"
        >
          Report an issue
        </a>
      ),
      key: "report-issue",
    },
  ];

  const headers = [
    {
      text: <VectechTextIconSvg className="logo-header" />,
    },
    { text: "Surveillance Activity", route: ROUTES.home },
    { text: "Image Catalog", route: ROUTES.image_catalog },
    { text: "Traps Admin", route: ROUTES.traps_and_locations },
    { text: "Alerts & Notifications", route: ROUTES.alerts_notifications },
    // Superusers only. The route itself is guarded and every endpoint behind it
    // is enforced server-side; hiding the link keeps it out of everyone else's
    // way rather than being the protection.
    ...(readSuperUser(user)
      ? [{ text: "Super User", route: ROUTES.superuser }]
      : []),
  ];

  const getUserIcon = () => {
    const org = user?.["https://mos-id.com/user_metadata"]?.organization;
    if (org?.startsWith("UKHSA-MEZE")) {
      return <UKHealthSecurityAgencySvg width={70} height={70} />;
    } else {
      return <UserSvg width={22} height={22} />;
    }
  };

  return (
    <>
      {contextHolder}
      {isAuthenticated && (
        <nav className="">
          <div className="nav-texts mt-6">
            {headers.map((header, index) => {
              const isActive =
                header.route &&
                (header.route === "/"
                  ? location.pathname === "/"
                  : location.pathname.includes(header.route));
              return (
                <Link
                  key={`nav-text-${index}`}
                  to={header.route || "/"}
                  className={isActive ? "active" : ""}
                >
                  <Title level={5}>{header.text}</Title>
                </Link>
              );
            })}
            <Dropdown
              menu={{ items: supportItems }}
              trigger={["hover"]}
              placement="bottom"
            >
              <span className="support-link">
                <Title level={5}>Support</Title>
              </span>
            </Dropdown>
          </div>

          <ul>
            {/* Left of the user's name: the organization you are acting as,
                then who you are. Renders nothing for anyone with a single
                organization. */}
            <li className="big-screens">
              <OrgSelector />
            </li>
            <li className="big-screens">
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Flex
                  align="center"
                  gap={8}
                  style={{ cursor: "pointer" }}
                  justify={"center"}
                >
                  <span className="m-auto">{getUserIcon()}</span>
                  <Title level={5} style={{ margin: 0 }}>
                    {user?.name}
                  </Title>
                </Flex>
              </Dropdown>
            </li>
          </ul>
        </nav>
      )}
    </>
  );
};

export default connector(Navbar);
